import type { Session } from "next-auth"
import { SessionProvider, signIn, signOut, useSession } from "next-auth/react"
import { AppContext } from "next/app"
import { useRouter } from "next/router"
import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react"
import type { KeycloakClaims } from "../nextauth/augmentation"
import {
  AuthInfo,
  KeycloakLogoutOptions,
  KeycloakOption,
  UserInfo,
} from "./types"

const defaultUserInfo = (): UserInfo => ({
  isAuthenticated: false,
  name: "",
  email: "",
  realmRoles: [],
  clientRoles: [],
  groups: [],
  outsourcingOrganizationId: undefined,
})

const defaultAuthInfo = (): AuthInfo => ({
  isAuthenticated: false,
  accessToken: "",
  refreshToken: "",
  idToken: "",
  identity: {},
  realmRoles: [],
  clientRoles: [],
  async logout() {},
  outsourcingOrganizationId: undefined,
})

/**
 * Keycloak access token 수명에 맞춰 세션(=accessToken)을 재검증하는 주기(초).
 * jwt 콜백이 만료 임박 시 refresh 하므로, 이 주기마다 신선한 토큰이 클라이언트로 전달된다.
 * 실제 access token TTL 에 맞춰 조정한다 (docs/packages/auth/architecture.md 3.3/3.5 참고).
 */
const REFETCH_INTERVAL_SEC = 240

/**
 * 포탈 "고객사 파트너 로그인"으로 진입한 탭을 표식하는 sessionStorage 키.
 * 표식이 있으면 로그아웃 시 공유 realm SSO 를 종료하지 않고(=federated logout 생략) returnTo 로 복귀해,
 * 포탈에서의 재임퍼소네이션이 재로그인 없이 동작하도록 한다. 파트너 앱(_app.tsx)에서 set, 여기서 read.
 */
export const IMPERSONATE_FLAG_KEY = "today.impersonate"
export const IMPERSONATE_RETURN_TO_KEY = "today.impersonate.returnTo"

/** federated logout(end-session) URL 구성을 위해 KeycloakOption 을 하위로 전달한다. */
const KeycloakOptionContext = createContext<KeycloakOption | undefined>(
  undefined
)

type Props = PropsWithChildren<
  KeycloakOption &
    AuthInitialProps & {
      loadingOverlay?: ReactNode
      allowAnonymousPaths?: {
        [path in string]: "exact" | "prefix"
      }
    }
>

/**
 * 인증 정보를 제공하는 프로바이더.
 *
 * @see useAuthentication()
 * @see useUserInfo()
 */
export function AuthenticationProvider({
  url,
  realm,
  clientId,
  authInitialProp,
  allowAnonymousPaths,
  loadingOverlay,
  children,
}: Props) {
  const keycloakOption = useMemo(
    () => ({ url, realm, clientId }),
    [url, realm, clientId]
  )
  return (
    <SessionProvider
      session={authInitialProp?.session}
      refetchInterval={REFETCH_INTERVAL_SEC}
      refetchOnWindowFocus
    >
      <RefreshTokenErrorHandler />
      <ImpersonationHandler />
      <KeycloakOptionContext.Provider value={keycloakOption}>
        <AnonymousGatekeeper
          allowAnonymousPaths={allowAnonymousPaths}
          loadingOverlay={loadingOverlay}
        >
          {children}
        </AnonymousGatekeeper>
      </KeycloakOptionContext.Provider>
    </SessionProvider>
  )
}

type AnonymousPermissionStatus = {
  allowAnonymous: boolean
}

const AnonymousPermissionContext = createContext<AnonymousPermissionStatus>({
  allowAnonymous: false,
})

function isAnonymousAllowed(
  path: string,
  allowAnonymousPaths?: Props["allowAnonymousPaths"]
): boolean {
  if (!allowAnonymousPaths) {
    return false
  }
  // 완전 일치 키이거나, prefix 타입 항목 중 하나가 경로의 접두사인 경우 허용.
  return (
    !!allowAnonymousPaths[path] ||
    Object.entries(allowAnonymousPaths).some(
      ([p, type]) => type === "prefix" && path.startsWith(p)
    )
  )
}

function AnonymousGatekeeper({
  allowAnonymousPaths,
  loadingOverlay,
  children,
}: PropsWithChildren<Pick<Props, "allowAnonymousPaths" | "loadingOverlay">>) {
  const router = useRouter()
  const path = router.route
  const { status } = useSession()
  const allowAnonymous = isAnonymousAllowed(path, allowAnonymousPaths)

  useEffect(() => {
    // 로그아웃 진행 중이면 미인증 상태여도 로그인으로 강제 이동하지 않는다(레이스 컨디션 방지).
    if (status === "loading" || status === "authenticated" || isLoggingOut) {
      return
    }
    // 미인증 + 익명 비허용 경로 → Keycloak 로그인으로 강제 이동.
    if (!allowAnonymous) {
      signIn("keycloak")
    }
  }, [status, allowAnonymous, path])

  return (
    <AnonymousPermissionContext.Provider value={{ allowAnonymous }}>
      {status === "authenticated" || allowAnonymous ? children : loadingOverlay}
    </AnonymousPermissionContext.Provider>
  )
}

export function useAnonymousPermissionStatus() {
  return useContext(AnonymousPermissionContext)
}

/**
 * refresh token 회전 실패(예: Keycloak SSO 세션이 외부에서 종료됨) 시 세션에 error 가 실린다.
 * 이때 재인증을 강제해 앱 세션을 정리한다. (구 @react-keycloak/ssr 의 "갱신 실패 → logout" 동작 보존.)
 *
 * 단, JWT 전략 특성상 access token 이 만료에 임박해 refresh 를 시도하기 전까지는 종료를 감지할 수 없어
 * 감지까지 access token 수명만큼의 지연이 존재한다. 즉각적인 원격 로그아웃이 필요하면
 * access token 수명/REFETCH_INTERVAL_SEC 을 줄이거나 OIDC back-channel logout 도입이 필요하다.
 */
function RefreshTokenErrorHandler() {
  const { data } = useSession()
  useEffect(() => {
    if (data?.error === "RefreshTokenError") {
      signIn("keycloak")
    }
  }, [data?.error])
  return null
}

/** returnTo(포탈 복귀 주소)는 URL 파라미터라 open-redirect 를 막기 위해 신뢰 호스트만 허용한다. */
function isTrustedReturnTo(value: string): boolean {
  try {
    const { protocol, hostname } = new URL(value)
    if (protocol !== "https:" && protocol !== "http:") return false
    // endsWith 만 쓰면 evilamazing.today·attacker-vtov.studio 같은 유사 도메인이 통과하므로
    // 정확히 일치하거나 점(.)으로 시작하는 서브도메인만 허용한다.
    return (
      hostname === "localhost" ||
      hostname === "vtov.studio" ||
      hostname.endsWith(".vtov.studio") ||
      hostname === "amazing.today" ||
      hostname.endsWith(".amazing.today")
    )
  } catch {
    return false
  }
}

/**
 * 포탈 "고객사 파트너 로그인"(?impersonate=1) 진입 처리.
 *
 * 임퍼소네이션은 운영자의 Keycloak 사용자 속성(client_id)을 바꾼 뒤 파트너로 진입한다. 그런데
 * next-auth JWT 세션이 캐시돼 있으면 다른 고객사로 바꿔도 토큰이 갱신되지 않아 처음 고객사로
 * 남는다. 세션이 확립되면 update() 로 refresh_token 회전을 강제해 변경된 속성을 토큰에 반영한다
 * (Keycloak 은 토큰 발급 때마다 사용자 속성을 재독한다 — 구 @react-keycloak/ssr 의 주기적
 * updateToken 과 동일 효과). 미인증이면 AnonymousGatekeeper 가 로그인으로 보내고, 복귀 후
 * 본 핸들러가 재실행된다.
 *
 * 또한 이 탭을 임퍼소네이션 세션으로 표식해 두어, 로그아웃 시 공유 realm SSO 를 종료하지 않고
 * returnTo(포탈)로 복귀하도록 한다(useFederatedLogout 참고).
 */
function ImpersonationHandler() {
  const { status, update } = useSession()
  const handledRef = useRef(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    if (params.get("impersonate") !== "1") return

    window.sessionStorage.setItem(IMPERSONATE_FLAG_KEY, "1")
    const returnTo = params.get("returnTo")
    if (returnTo && isTrustedReturnTo(returnTo)) {
      window.sessionStorage.setItem(IMPERSONATE_RETURN_TO_KEY, returnTo)
    }

    if (status !== "authenticated" || handledRef.current) return
    handledRef.current = true

    update().finally(() => {
      // 재진입/공유 방지를 위해 처리 후 impersonate 파라미터를 URL 에서 제거한다.
      const url = new URL(window.location.href)
      url.searchParams.delete("impersonate")
      url.searchParams.delete("returnTo")
      window.history.replaceState(null, "", url.toString())
    })
  }, [status, update])
  return null
}

export interface AuthInitialProps {
  authInitialProp: {
    session?: Session
  }
}

/**
 * `_app` 의 `getInitialProps` 래퍼. next-auth 전환 후에는 SSR 쿠키 주입이 불필요하며
 * (SessionProvider 가 클라이언트에서 세션을 자동 fetch), 시그니처 호환을 위해 유지한다.
 */
export function withAuth(
  getInitialProps: (context: AppContext) => Promise<any>
): (context: AppContext) => Promise<any> {
  return async (context: AppContext) => {
    return {
      ...(await getInitialProps(context)),
      authInitialProp: {},
    }
  }
}

function mapSessionToAuthInfo(
  session: Session | null | undefined,
  logout: AuthInfo["logout"]
): AuthInfo {
  const claims: KeycloakClaims | undefined = session?.claims
  if (!claims) {
    return { ...defaultAuthInfo(), logout }
  }
  return {
    isAuthenticated: claims.isAuthenticated,
    accessToken: session?.accessToken ?? "",
    refreshToken: session?.refreshToken ?? "",
    idToken: session?.idToken ?? "",
    identity: claims.identity ?? {},
    realmRoles: claims.realmRoles ?? [],
    clientRoles: claims.clientRoles ?? [],
    clientId: claims.authClientId,
    logout,
    outsourcingOrganizationId: claims.outsourcingOrganizationId,
  }
}

function mapSessionToUserInfo(session: Session | null | undefined): UserInfo {
  const claims = session?.claims
  if (!claims) {
    return defaultUserInfo()
  }
  return {
    isAuthenticated: claims.isAuthenticated,
    name: claims.name ?? "",
    email: claims.email ?? "",
    phoneNumber: claims.phoneNumber,
    realmRoles: claims.realmRoles ?? [],
    clientRoles: claims.clientRoles ?? [],
    groups: claims.groups ?? [],
    clientId: claims.userClientId,
    sellerName: claims.sellerName,
    outsourcingOrganizationId: claims.outsourcingOrganizationId,
  }
}

/**
 * 로그아웃 진행 중 플래그. signOut() 으로 세션이 미인증으로 바뀐 직후 AnonymousGatekeeper 가
 * 로그인 페이지로 리다이렉트하는 레이스 컨디션을 막는다. 로그아웃은 항상 전체 페이지 이동으로
 * 끝나므로 별도 초기화는 불필요하다(다음 로드 시 모듈이 새로 초기화됨).
 */
let isLoggingOut = false

/**
 * next-auth 앱 세션과 Keycloak SSO 세션을 함께 종료하는 로그아웃 래퍼.
 * next-auth signOut() 만으로는 IdP 세션이 남으므로 end-session 엔드포인트로 이동한다.
 */
function useFederatedLogout(): AuthInfo["logout"] {
  const { data } = useSession()
  const option = useContext(KeycloakOptionContext)
  const idToken = data?.idToken
  return useCallback(
    async (options?: KeycloakLogoutOptions) => {
      isLoggingOut = true
      const redirectUri =
        options?.redirectUri ??
        (typeof window !== "undefined" ? window.location.origin : "/")

      // 임퍼소네이션 세션: 공유 realm SSO 를 보존하기 위해 next-auth 로컬 세션만 정리하고
      // end-session(federated logout) 을 호출하지 않는다. 포탈로 복귀하면 다른 고객사로
      // 재임퍼소네이션할 때 재로그인 없이 silent 로 동작한다.
      const isImpersonation =
        typeof window !== "undefined" &&
        window.sessionStorage.getItem(IMPERSONATE_FLAG_KEY) === "1"
      if (isImpersonation) {
        // sink 직전 재검증: 신뢰 호스트가 아니면 파트너 origin 으로 fallback (open-redirect 방지).
        const stored = window.sessionStorage.getItem(IMPERSONATE_RETURN_TO_KEY)
        const returnTo =
          stored && isTrustedReturnTo(stored) ? stored : redirectUri
        window.sessionStorage.removeItem(IMPERSONATE_FLAG_KEY)
        window.sessionStorage.removeItem(IMPERSONATE_RETURN_TO_KEY)
        await signOut({ redirect: false })
        window.location.href = returnTo
        return
      }

      await signOut({ redirect: false })
      if (option) {
        const params = new URLSearchParams({
          post_logout_redirect_uri: redirectUri,
        })
        if (idToken) {
          params.set("id_token_hint", idToken)
        }
        // option.url 끝의 슬래시를 제거해 double slash 를 방지한다.
        const baseUrl = option.url.replace(/\/$/, "")
        window.location.href = `${baseUrl}/realms/${
          option.realm
        }/protocol/openid-connect/logout?${params.toString()}`
        return
      }
      window.location.href = redirectUri
    },
    [idToken, option]
  )
}

/**
 * 현재 인증 정보를 반환한다.
 *
 * @requires AuthenticationProvider
 */
export function useAuthentication(): AuthInfo {
  const { data } = useSession()
  const logout = useFederatedLogout()
  return useMemo(() => mapSessionToAuthInfo(data, logout), [data, logout])
}

/**
 * 현재 유저 정보를 반환한다.
 *
 * @requires AuthenticationProvider
 */
export function useUserInfo(): UserInfo {
  const { data } = useSession()
  return useMemo(() => mapSessionToUserInfo(data), [data])
}
